import React, { FC, useCallback, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  CreditFilterIcon,
  ExpiryReminderDateIcon,
  SearchIcon,
  SendReminderActionIcon,
} from "customIcons";
import { CloseIcon } from "@features/common/SvgIcons";
import FilterDropDown from "@features/creditReports/filterDropdown/FilterDropDown";
import styles from "./SendReminderTable.module.scss";
import { formatDate } from "@utils/common";
import EmptyTable from "@features/common/emptyTable/EmptyTable";

// ============================================================================
// Types & Interfaces
// ============================================================================
interface SendReminderTableProps {
  data: any;
  isLoading: boolean;
  searchTerm: any;
  onSearchChange: (val: any) => void;
  selectedPromo: any;
  onPromoSelected: (val: any, name?: string) => void;
  onSendReminder: (credit_id: any) => void;
  promotions?: any[];
  nextPageUrl?: string | null;
  isFetchingNextPage?: boolean;
  setCurrentFetchUrl?: React.Dispatch<React.SetStateAction<string | null>>;
  onPromoSearchChange?: (val: string) => void;
  promoSearchTerm?: string;
  promoName?: string;
}

// ============================================================================
// Component
// ============================================================================
const SendReminderTable: FC<SendReminderTableProps> = ({
  data,
  isLoading,
  searchTerm,
  onSearchChange,
  selectedPromo,
  onPromoSelected,
  onSendReminder,
  promotions,
  nextPageUrl,
  isFetchingNextPage,
  setCurrentFetchUrl,
  onPromoSearchChange,
  promoSearchTerm,
  promoName,
}) => {
  // ============================================================================
  // State
  // ============================================================================
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================================================
  // Computed Values
  // ============================================================================
  const options = promotions?.map((promo) => ({
    label: promo.name,
    value: promo.id,
  }));

  // ============================================================================
  // Event Handlers
  // ============================================================================
  /**
   * Handles mouse enter event for filter dropdown
   * Clears any pending close timeout and opens the dropdown
   */
  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  /**
   * Handles mouse leave event for filter dropdown
   * Sets a timeout to close the dropdown after 300ms
   */
  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300);
  }, []);

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className={`${styles["reports-table"]} users-table`}>
      {/* Action Bar with Filters */}
      <div className={styles["reports-table__action-bar"]}>
        <div className={styles["reports-table__action-bar__left"]}></div>
        <div className={styles["reports-table__action-bar__right"]}>
          <div
            className={styles["reports-table__action-bar__right__filter"]}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <CreditFilterIcon />
            <span>{promoName ? promoName : "Filter by Promotion Name"}</span>
            {promoName && (
              <span
                className={
                  styles["reports-table__action-bar__right__filter__close-icon"]
                }
                onClick={() => {
                  onPromoSelected(null);
                }}
              >
                <CloseIcon />
              </span>
            )}

            {isOpen && (
              <FilterDropDown
                selectedValue={selectedPromo}
                options={options}
                onSelect={onPromoSelected}
                onPromoSearchChange={onPromoSearchChange}
                promoSearchTerm={promoSearchTerm}
                nextPageUrl={nextPageUrl}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={() => {
                  if (nextPageUrl && setCurrentFetchUrl) {
                    setCurrentFetchUrl(nextPageUrl);
                  }
                }}
              />
            )}
          </div>
          {/* Search Input */}
          <div className={styles["reports-table__action-bar__right__search"]}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by Credit ID / User Name"
              className={
                styles["reports-table__search-bar-container__inner-input"]
              }
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
            {searchTerm && (
              <div
                className={
                  styles["reports-table__action-bar__right__search__close-icon"]
                }
                onClick={() => onSearchChange?.("")}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: "20px",
                }}
              >
                <CloseIcon />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table Wrapper */}
      <div
        className={styles["reports-table__table-wrapper"]}
        style={
          isLoading
            ? { height: "393px", position: "relative" }
            : { maxHeight: "393px", overflowY: "auto", position: "relative" }
        }
      >
        <TableContainer sx={{ marginTop: "16px", padding: "0 8px" }}>
          <Table
            className={`${styles["reports-table__table"]} send-reminder-table`}
            stickyHeader
          >
            {/* Table Header */}
            <TableHead>
              <TableRow
                className={styles["reports-table__head-row"]}
                sx={{
                  "& th": {
                    backgroundColor: "#F5F5F5",
                    zIndex: 1,
                    padding: "0px 16px",
                    borderBottom: "none",
                  },
                }}
              >
                <TableCell>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <div>Expiry Date</div>
                    <Tooltip
                      title="Credits nearing expiry will be highlighted.
  Expired Credits are not displayed on this screen."
                      placement="top"
                      arrow
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [80, -8], // Positive value moves tooltip to the right
                              },
                            },
                          ],
                        },
                        tooltip: {
                          sx: {
                            backgroundColor: "#000",
                            color: "#fff",
                            fontSize: "9px",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            width: "190px",
                          },
                        },
                        arrow: {
                          sx: {
                            color: "#000",
                            "&::before": {
                              backgroundColor: "#000",
                            },
                          },
                        },
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignSelf: "center",
                          cursor: "pointer",
                        }}
                      >
                        <ExpiryReminderDateIcon />
                      </div>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell>Credit ID</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>User Status</TableCell>
                <TableCell>Promotion Name</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body with Loading State */}
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: "none" }}>
                    <CircularProgress
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : data?.length > 0 ? (
              <TableBody>
                {data?.map((item: any, index: number) => {
                  // Check if expiration date is within next 7 days
                  const expirationDate = dayjs(item?.expiration_date);
                  const today = dayjs();
                  const sevenDaysFromNow = today.add(7, "day");
                  const isWithinSevenDays =
                    expirationDate.isAfter(today.subtract(1, "day")) &&
                    expirationDate.isBefore(sevenDaysFromNow.add(1, "day"));

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <span
                            style={{
                              color: isWithinSevenDays ? "#F70" : "#000",
                            }}
                          >
                            {formatDate(item?.expiration_date)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{item?.id}</TableCell>
                      <TableCell>
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <span>{item?.name}</span>
                          <span style={{ color: "#868785", fontWeight: "500" }}>
                            {item?.credit_customer_username}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        style={{
                          color:
                            item?.user_status === "Active"
                              ? "#34C759"
                              : "#868785",
                        }}
                      >
                        {item?.user_status}
                      </TableCell>
                      <TableCell>{item?.promotion}</TableCell>
                      <TableCell
                        align="left"
                        style={{ cursor: "pointer" }}
                        onClick={() => onSendReminder(item?.id)}
                      >
                        <Tooltip
                          title={
                            item?.user_status === "Active"
                              ? "Send Expiry Reminder"
                              : "Send Activation Reminder"
                          }
                          placement="bottom"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: "#000",
                                color: "#fff",
                                fontSize: "10px",
                                padding: "4px 8px",
                                borderRadius: "4px",
                              },
                            },
                            arrow: { sx: { color: "#000" } },
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              backgroundColor: "#F5F5F5",
                              padding: "4px 8px",
                              borderRadius: "8px",
                              width: "32px",
                              height: "32px",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <SendReminderActionIcon />
                          </div>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            ) : (
              <EmptyTable colSpan={6} />
            )}
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default SendReminderTable;
